import { createGlobalState } from '@vueuse/core';
import { ref, nextTick } from 'vue';
import { useToasted } from '~/composables/useToasted';
import { messageStandardsClient } from '~/services';
import type {
    MessageStandardRulesType,
    MessageStandardType,
} from '~/services/messageStandards/messageStandardsClient.types';
import { catchRequestError } from '~/utils/request';

export const countMessageStandardRules = (rules?: MessageStandardRulesType | null) => {
    if (!rules) {
        return 0;
    }

    return [
        rules.block_images,
        rules.block_files,
        rules.maximum_emoji !== null,
        rules.maximum_sms_segments !== null,
        rules.maximum_characters !== null,
        rules.blocked_words !== null,
        rules.send_frequency_cadence !== null && rules.send_frequency_limit !== null,
        rules.min_scheduling_days !== null,
        rules.max_scheduling_weeks !== null,
        rules.permitted_sending_windows !== null,
    ].filter(Boolean).length;
};

export const defaultPermittedSendingWindows = () => {
    return [
        { day: 'mon', start: 9, end: 18 },
        { day: 'tue', start: 9, end: 18 },
        { day: 'wed', start: 9, end: 18 },
        { day: 'thu', start: 9, end: 18 },
        { day: 'fri', start: 9, end: 18 },
    ];
};

export const defaultMessageStandardRules = (): MessageStandardRulesType => ({
    block_images: false,
    block_files: false,
    maximum_emoji: null,
    maximum_sms_segments: null,
    maximum_characters: null,
    blocked_words: null,
    send_frequency_cadence: null,
    send_frequency_limit: null,
    min_scheduling_days: null,
    max_scheduling_weeks: null,
    permitted_sending_windows: null,
});

export const defaultMessageStandard = (): MessageStandardType => {
    return {
        name: '',
        description: '',
        marketing_rules: defaultMessageStandardRules(),
        transactional_config: 'block_all_sending',
        transactional_rules: defaultMessageStandardRules(),
    };
};

export const readMessageStandard = (messageStandard: MessageStandardType) => {
    // necessary to show the filters on frontend, on submit we count each applied rule and send null if there are none
    if (!messageStandard.marketing_rules) {
        messageStandard.marketing_rules = defaultMessageStandardRules();
    }

    if (!messageStandard.transactional_rules) {
        messageStandard.transactional_rules = defaultMessageStandardRules();
    }

    // backend returns empty array after a single rule was set, we use null to know if the toggle should be "No" or "Yes"
    const possibleEmptyArrayKeys = ['blocked_words', 'permitted_sending_windows'] as const;
    possibleEmptyArrayKeys.forEach((key) => {
        if (messageStandard.marketing_rules?.[key]?.length === 0) {
            messageStandard.marketing_rules[key] = null;
        }

        if (messageStandard.transactional_rules?.[key]?.length === 0) {
            messageStandard.transactional_rules[key] = null;
        }
    });

    return messageStandard;
};

export const useMessageStandardDetails = createGlobalState(() => {
    const toasted = useToasted();
    const teamId = Spark.state.currentTeam.id;

    const messageStandard = ref<MessageStandardType>(defaultMessageStandard());
    const loading = ref(true);

    let onMountedMessageStandardCallback: CallableFunction = () => {};
    const onMountedMessageStandard = (callback: CallableFunction) => {
        onMountedMessageStandardCallback = callback;
    };

    const mount = async (messageStandardId: number) => {
        await nextTick();

        if (!messageStandardId) {
            messageStandard.value = defaultMessageStandard();
            loading.value = false;
            onMountedMessageStandardCallback?.();
            return;
        }

        try {
            loading.value = true;
            const response = await messageStandardsClient.getMessageStandard({ teamId }, Number(messageStandardId));
            messageStandard.value = readMessageStandard(response.data);

            loading.value = false;
            onMountedMessageStandardCallback?.();
        } catch (e) {
            toasted?.error(catchRequestError(e));
        }
    };

    const unmount = () => {
        messageStandard.value = defaultMessageStandard();
        loading.value = false;
    };

    return {
        loading,
        messageStandard,
        mount,
        unmount,
        onMountedMessageStandard,
    };
});
